import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetWorkstreams } from '../redux/dataroomSlice';
import {
    ASK_CONTEXT_MODE,
    applyAskContext,
    ensureAskContextProject,
    fetchIntelligenceScopeCatalog,
    fetchIntelligenceScopeStats,
    setAskContextMode,
    setAskCustomContext,
    setAskSelectedWorkstreams,
} from '../redux/intelligenceSlice';
import {
    selectedWorkstreamIds,
    workstreamOptionsFromList,
} from '../utils/aiWardenScope';
import {
    getUnionCustomWorkstreamIds,
    resolveCustomContextScope,
} from '../utils/lensIntelligenceCustomContext';

export const CONTEXT_MODE = ASK_CONTEXT_MODE;

export function useLensIntelligenceScope(projectId) {
    const dispatch = useDispatch();
    const { workstreams } = useSelector(state => state.dataroom);
    const {
        askContextProjectId,
        askContextMode,
        askSelectedWorkstreams,
        askCustomContext,
        scopeCatalogProjectId,
        scopeCatalog,
        scopeCatalogLoading,
        scopeStatsProjectId,
        scopeStats,
        scopeModalFiles,
        scopeLoading,
    } = useSelector(state => state.intelligence) || {};

    const contextMode = askContextMode || CONTEXT_MODE.ENTIRE_PROJECT;
    const selectedWorkstreams = Array.isArray(askSelectedWorkstreams)
        ? askSelectedWorkstreams
        : [];
    const customContext = askCustomContext;
    const modalFiles = Array.isArray(scopeModalFiles) ? scopeModalFiles : [];

    const allWorkstreamIds = useMemo(
        () => (workstreams || []).map(w => String(w.id)).filter(Boolean),
        [workstreams],
    );

    const effectiveWorkstreamIds = useMemo(() => {
        if (contextMode === CONTEXT_MODE.CUSTOM) {
            return getUnionCustomWorkstreamIds(customContext, allWorkstreamIds);
        }
        return selectedWorkstreamIds(selectedWorkstreams, allWorkstreamIds);
    }, [contextMode, customContext, selectedWorkstreams, allWorkstreamIds]);

    const hasWorkstreamsSelected = effectiveWorkstreamIds.length > 0;

    const statsForProject =
        projectId && String(scopeStatsProjectId) === String(projectId)
            ? scopeStats
            : { documents: 0, irlResponses: 0, observations: 0 };

    // Prefer scoped stats; while refreshing after navigation, keep last same-project stats.
    // Fall back to full catalog counts when scoped stats are not yet for this project.
    const catalogForProject =
        projectId && String(scopeCatalogProjectId) === String(projectId)
            ? scopeCatalog
            : null;

    const displayScopeStats = useMemo(() => {
        if (projectId && String(scopeStatsProjectId) === String(projectId)) {
            return statsForProject;
        }
        if (catalogForProject) {
            return {
                documents: (catalogForProject.documents || []).length,
                irlResponses: (catalogForProject.irls || []).length,
                observations: (catalogForProject.observations || []).length,
            };
        }
        return { documents: 0, irlResponses: 0, observations: 0 };
    }, [projectId, scopeStatsProjectId, statsForProject, catalogForProject]);

    useEffect(() => {
        if (projectId) {
            dispatch(ensureAskContextProject(projectId));
            dispatch(GetWorkstreams(projectId));
            dispatch(fetchIntelligenceScopeCatalog({ projectId }));
        }
    }, [projectId, dispatch]);

    useEffect(() => {
        if (
            contextMode === CONTEXT_MODE.ENTIRE_PROJECT &&
            workstreams?.length &&
            String(askContextProjectId) === String(projectId) &&
            !selectedWorkstreams.length
        ) {
            dispatch(setAskSelectedWorkstreams(workstreamOptionsFromList(workstreams)));
        }
    }, [
        contextMode,
        workstreams,
        askContextProjectId,
        projectId,
        selectedWorkstreams.length,
        dispatch,
    ]);

    useEffect(() => {
        if (!projectId) return undefined;
        if (contextMode === CONTEXT_MODE.CUSTOM) {
            dispatch(
                fetchIntelligenceScopeStats({
                    projectId,
                    contextMode,
                    customContext,
                    allWorkstreamIds,
                }),
            );
            return undefined;
        }
        if (!hasWorkstreamsSelected) return undefined;
        dispatch(
            fetchIntelligenceScopeStats({
                projectId,
                contextMode,
                selectedWorkstreams,
                allWorkstreamIds,
            }),
        );
        return undefined;
    }, [
        projectId,
        contextMode,
        customContext,
        selectedWorkstreams,
        allWorkstreamIds,
        hasWorkstreamsSelected,
        dispatch,
    ]);

    const setContextMode = useCallback(
        mode => {
            dispatch(setAskContextMode(mode));
        },
        [dispatch],
    );

    const setSelectedWorkstreams = useCallback(
        next => {
            dispatch(setAskSelectedWorkstreams(next));
        },
        [dispatch],
    );

    const setCustomContext = useCallback(
        next => {
            dispatch(setAskCustomContext(next));
        },
        [dispatch],
    );

    const resolveScope = useCallback(() => {
        if (contextMode === CONTEXT_MODE.CUSTOM) {
            return resolveCustomContextScope(customContext, allWorkstreamIds, workstreams);
        }

        const wsNums = effectiveWorkstreamIds.map(Number).filter(Number.isFinite);
        const isEntire =
            contextMode === CONTEXT_MODE.ENTIRE_PROJECT ||
            (allWorkstreamIds.length > 0 && wsNums.length >= allWorkstreamIds.length);

        return {
            type: isEntire ? 'all_workstreams' : 'workstream',
            workstreamIds: wsNums,
            includeIrl: true,
            includeObservations: true,
            fileIds: null,
            folderIds: null,
            label: isEntire
                ? 'Entire Project'
                : wsNums.length === 1
                  ? 'Selected workstream'
                  : `${wsNums.length} workstreams`,
        };
    }, [effectiveWorkstreamIds, allWorkstreamIds.length, contextMode, customContext, workstreams]);

    const citationDisplayCtx = useMemo(
        () => ({
            workstreams,
            modalFiles,
        }),
        [workstreams, modalFiles],
    );

    return {
        contextMode,
        setContextMode,
        selectedWorkstreams,
        setSelectedWorkstreams,
        customContext,
        setCustomContext,
        applyAskContext: payload =>
            dispatch(applyAskContext({ ...payload, projectId })),
        workstreams,
        allWorkstreamIds,
        effectiveWorkstreamIds,
        hasWorkstreamsSelected,
        scopeLoading: Boolean(scopeLoading),
        scopeStats: displayScopeStats,
        modalFiles,
        resolveScope,
        citationDisplayCtx,
        scopeCatalog: catalogForProject || { documents: [], irls: [], observations: [] },
        scopeCatalogLoading: Boolean(scopeCatalogLoading),
        refreshScopeCatalog: () =>
            projectId ? dispatch(fetchIntelligenceScopeCatalog({ projectId })) : undefined,
    };
}
